import { useState } from 'react';
import { Header, Breadcrumb, LocaleSwitcher } from 'components';
import LocationAwareSearch from 'components/LocationAwareSearch';
import { Container } from 'ui';
import { useTranslation } from 'react-i18next';
import {
  formatOrderCriteria,
  initialLocale,
  settingRequestState,
  stateEnum,
} from 'utils';
import { isEqual } from 'lodash';
import { useSearchParams, useApp } from 'hooks';
import { Stack } from '@koob/margaret';
import SettingRequestsByStateAndTheme from 'components/SettingRequest/SettingRequestsListByState';
import { useAsync } from 'react-async';
import {
  KoobCreateButton,
  KoobDataTable,
  KoobTableAction,
  KoobInputSegmented,
  KoobListPageLayout,
  koobTableUrlSort,
} from '@koob/ui';
import { getSegments } from 'api/node/segments';
import { PER_PAGE_FOR_LIST } from 'constants';

const SegmentsList = () => {
  const { t } = useTranslation('experienceSegment');
  const { isAllowedTo, user, locales } = useApp();

  const [searchParams, setSearchParams] = useSearchParams();
  const { search, state } = searchParams;
  const page = Number(searchParams.page) || 1;
  const perPage = Number(searchParams.perPage) || PER_PAGE_FOR_LIST;
  const setPage = (page) => setSearchParams({ ...searchParams, page });
  const setPerPage = (perPage) =>
    setSearchParams({ ...searchParams, perPage, page: undefined });
  const { sort, onSortChange } = koobTableUrlSort(
    searchParams,
    setSearchParams,
  );
  const [totalPages, setTotalPages] = useState();

  const currentLocale =
    (locales ?? []).find((locale) => locale.value === searchParams.locale) ??
    initialLocale;
  const setCurrentLocale = (locale) =>
    setSearchParams({
      ...searchParams,
      locale: locale?.value,
      page: undefined,
    });

  const filters = {
    search,
    locale: currentLocale?.value,
    page: page,
    discarded: state === stateEnum.ARCHIVED,
    perPage: perPage,
    order: formatOrderCriteria(searchParams).column.toLowerCase(),
    direction: formatOrderCriteria(searchParams).direction.toLowerCase(),
  };

  const { data: experienceSegments, isLoading } = useAsync({
    promiseFn: getSegments,
    filters,
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
    onResolve: (response) => {
      setTotalPages(response?.headers?.xTotalPages);
    },
  });
  const totalCount = Number(experienceSegments?.headers?.xTotalCount || 0);
  const handleStateChange = (newState) => {
    setSearchParams({
      ...searchParams,
      search: undefined,
      state: newState,
      page: undefined,
    });
  };

  const stateOptions = [
    { value: null, label: t('misc:active') },
    { value: stateEnum.ARCHIVED, label: t('misc:archived') },
    { value: stateEnum.PENDING, label: t('misc:pending') },
    { value: stateEnum.REFUSED, label: t('misc:refused') },
  ];

  const columns = [
    {
      key: 'enDisplayName',
      label: (
        <Stack gutterSize={0.375} alignY="baseline">
          <span>{t('name')}</span>
          <span className={`flag-icon flag-icon-gb`} />
        </Stack>
      ),
      sortable: true,
    },
    {
      key: 'displayName',
      label: (
        <Stack gutterSize={0.375} alignY="baseline">
          <span>{t('name')}</span>
          <span className={`flag-icon flag-icon-${currentLocale.alpha2}`} />
        </Stack>
      ),
      sortable: true,
    },
  ];

  const actions =
    isAllowedTo('manageExperienceSettings') && state !== stateEnum.ARCHIVED
      ? (node) => (
          <>
            <KoobTableAction
              icon="edit"
              label={t('misc:edit')}
              to={`${node?.id ?? ''}/edit`}
              state={currentLocale}
            />
            <KoobTableAction
              icon="archive"
              variant="warning"
              label={t('misc:archive')}
              to={`${node?.id ?? ''}/archive`}
            />
          </>
        )
      : undefined;

  return (
    <>
      <Header leftElement={<Breadcrumb />} />
      <Container variant="main" size="full">
        <KoobListPageLayout
          width="narrow"
          subtitle={t('misc:resultsCount', { count: Number(totalCount) || 0 })}
          actions={
            <>
              {isAllowedTo('manageExperienceSettings') && (
                <KoobCreateButton
                  to="new"
                  disabled={currentLocale.value !== initialLocale.value}
                >
                  {t('newSegment')}
                </KoobCreateButton>
              )}
              {user?.role === 'dmc_admin' && (
                <KoobCreateButton
                  relative="path"
                  to="../setting-request/new/segment"
                >
                  {t('newSettingRequest')}
                </KoobCreateButton>
              )}
            </>
          }
          search={<LocationAwareSearch name="search" />}
          filters={
            <KoobInputSegmented
              options={stateOptions}
              value={state ?? null}
              onChange={handleStateChange}
            />
          }
          toolbarActions={
            <LocaleSwitcher
              queryLocale={currentLocale}
              updateQueryLocale={setCurrentLocale}
            />
          }
        >
          {settingRequestState.includes(state) ? (
            <SettingRequestsByStateAndTheme
              settingName={'segment'}
              kind={'experience'}
              state={state}
            />
          ) : (
            <div className="max-h-[200vh]">
              <KoobDataTable
                columns={columns}
                data={experienceSegments?.data ?? []}
                actions={actions}
                loading={isLoading}
                sort={sort}
                onSortChange={onSortChange}
                pagination={{
                  page,
                  setPage,
                  totalPages,
                  totalCount,
                  perPage,
                  setPerPage,
                }}
              />
            </div>
          )}
        </KoobListPageLayout>
      </Container>
    </>
  );
};

export default SegmentsList;
